import type { StationTransfer } from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeftRight, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReasonActionDialog } from "../../features/platform/billing-dialogs.js";
import { TransferDialog } from "../../features/platform/ops-dialogs.js";
import {
  usePlatformPermissions,
  usePlatformTransfers,
  useRefreshPlatform,
} from "../../features/platform/queries.js";
import { Field, PlatformPage } from "../../features/platform/shared.js";
import { Approval, TRANSFER_STATUS_TONES, TransferReport } from "../../features/platform/transfers.js";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { formatDate, formatDateTime } from "../../lib/format.js";
import { useStepUp } from "../../lib/step-up.js";

export const Route = createFileRoute("/platform/devirler")({ component: TransfersRoute });

/**
 * İstasyon devirleri (Prompt 24 web): platform ekibi isteği açar, DEVREDEN ve DEVRALAN kiracının yetkilileri kendi
 * panellerinden onaylar; iki onay tamamlanınca devir tarihinde yürütülür ve sonuç raporu (taşınan / kalan / uyarılar)
 * üretilir. Eski kiracının geçmiş verisi onda kalır.
 */
function TransfersRoute(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPlatform();
  const withStepUp = useStepUp();
  const permissions = usePlatformPermissions();
  const canManage = permissions.has("platform.transfers.manage");
  const transfers = usePlatformTransfers();
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<StationTransfer | null>(null);
  const today = toIstanbulDate(new Date());
  const execute = useMutation({
    mutationFn: (id: string) =>
      withStepUp(
        async () => (await api.POST("/platform/transfers/{id}/execute", { params: { path: { id } } })).data!,
      ),
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("platform.transfers.executed") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <PlatformPage
      title={t("platform.transfers.title")}
      description={t("platform.transfers.description")}
      permission="platform.tenants.read"
      actions={
        canManage ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("platform.transfers.new")}
          </Button>
        ) : null
      }
    >
      {transfers.error ? (
        <Alert tone="danger">{errorMessage(transfers.error)}</Alert>
      ) : transfers.isPending ? (
        <SkeletonRows rows={4} />
      ) : transfers.data.length === 0 ? (
        <EmptyState headingLevel={2} icon={<ArrowLeftRight />} title={t("platform.transfers.empty")} />
      ) : (
        <ul className="grid gap-3">
          {transfers.data.map((transfer) => {
            const approved = transfer.sourceApproval !== null && transfer.targetApproval !== null;
            const due = transfer.effectiveDate <= today;
            return (
              <li key={transfer.id}>
                <Card className="grid gap-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h2 className="text-base font-semibold">
                      {transfer.sourceStation.code} · {transfer.sourceStation.name}
                    </h2>
                    <StatusBadge tone={TRANSFER_STATUS_TONES[transfer.status]}>
                      {t(`platform.transfers.status.${transfer.status}`)}
                    </StatusBadge>
                  </div>
                  <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label={t("platform.fields.sourceTenant")}>{transfer.sourceTenant.name}</Field>
                    <Field label={t("platform.fields.targetTenant")}>
                      {transfer.targetTenant.name} · {transfer.targetCompany.name}
                    </Field>
                    <Field label={t("platform.fields.effectiveDate")}>
                      {formatDate(transfer.effectiveDate)}
                    </Field>
                    <Field label={t("platform.fields.requestedBy")}>{transfer.requestedBy ?? "—"}</Field>
                    <Field label={t("platform.transfers.sourceApproval")}>
                      <Approval value={transfer.sourceApproval} />
                    </Field>
                    <Field label={t("platform.transfers.targetApproval")}>
                      <Approval value={transfer.targetApproval} />
                    </Field>
                    {transfer.note ? <Field label={t("platform.fields.note")}>{transfer.note}</Field> : null}
                    {transfer.cancelReason ? (
                      <Field label={t("platform.fields.cancelReason")}>{transfer.cancelReason}</Field>
                    ) : null}
                    {transfer.completedAt ? (
                      <Field label={t("platform.fields.completedAt")}>
                        {formatDateTime(transfer.completedAt)}
                      </Field>
                    ) : null}
                  </dl>
                  {transfer.report ? <TransferReport report={transfer.report} /> : null}
                  {transfer.status === "PENDING" && canManage ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        disabled={!approved || !due}
                        loading={execute.isPending && execute.variables === transfer.id}
                        onClick={() => execute.mutate(transfer.id)}
                      >
                        {t("platform.transfers.execute")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setCancelling(transfer)}>
                        {t("platform.transfers.cancel")}
                      </Button>
                      {!approved ? (
                        <span className="text-sm text-muted-foreground">
                          {t("platform.transfers.waitingApprovals")}
                        </span>
                      ) : !due ? (
                        <span className="text-sm text-muted-foreground">
                          {t("platform.transfers.waitingDate")}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </Card>
              </li>
            );
          })}
        </ul>
      )}
      {creating ? <TransferDialog open onOpenChange={setCreating} /> : null}
      {cancelling ? (
        <ReasonActionDialog
          open
          onOpenChange={(open) => !open && setCancelling(null)}
          title={t("platform.transfers.cancelTitle")}
          description={t("platform.transfers.cancelDescription")}
          submitLabel={t("platform.transfers.cancel")}
          successTitle={t("platform.transfers.cancelled")}
          run={(reason) =>
            api.POST("/platform/transfers/{id}/cancel", {
              params: { path: { id: cancelling.id } },
              body: { reason },
            })
          }
        />
      ) : null}
    </PlatformPage>
  );
}
